import React, { useState, useEffect } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep, getSyllables } from "../shared/util";
import { Container, Word } from "../shared";
import useConversation from "../shared/useConversation";
import useValidation from "../shared/useValidation";
import "./Rhyming.scss";
import icon from "./rhyming-icon.png";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen } from "Services/Listen";
import {getUserData} from "Services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function RhymingValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.rhyming;

  const isValid = useValidation(props.version, props.wordMetadata, {
    1: ["RHYME", "RHYME_PHON", "RHYME_GRAPHEMIC_BREAKDOWN"],
  });

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!isValid) close();
  }, []);

  if (!isValid) return null;

  const Version = {
    1: Rhyming,
  }[props.version];

  return <Version {...props} close={close} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function Rhyming(props) {
  const { word, wordMetadata, speech } = props;
  const {
    PHON,
    RHYME,
    GRAPHEMIC_BREAKDOWN,
    RHYME_PHON,
    RHYME_GRAPHEMIC_BREAKDOWN,
  } = wordMetadata;

  const syllables = getSyllables(PHON, GRAPHEMIC_BREAKDOWN);
  const rhymeSyllables = getSyllables(RHYME_PHON, RHYME_GRAPHEMIC_BREAKDOWN);

  const [stage, setStage] = useState(0);
  const [syllable, setSyllable] = useState("");

  const { speak, speakSyllables, listenWord } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speak(ttsStrings.describe, true));
            await cancellablePromise(speak({textToSpeak: RHYME, skipTranslation: true}, true));
          }
          else await cancellablePromise(speak(`${ttsStrings.describe} "${RHYME}".`));

          await cancellablePromise(sleep(1500));
          setStage(2);
          break;
        case 2: {
          if(getUserData().bilingualMode) {
            await cancellablePromise(speak(ttsStrings.ask_word_1, true));
            await cancellablePromise(speak({textToSpeak: RHYME, skipTranslation: true}, true));
          }
          else await cancellablePromise(speak(`${ttsStrings.ask_word_1} "${RHYME}".`));

          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "match") await cancellablePromise(speak(ttsStrings.correct));
          if (["struggle", "silence"].includes(status)) setStage(3);
          else props.close();
          break;
        }
        case 3:
          // on miss or silence
          await cancellablePromise(speak(ttsStrings.struggle));
          await cancellablePromise(speakSyllables({
            phon: PHON,
            onSpeak: (i) => setSyllable(syllables[i]),
          }));
          await cancellablePromise(speak(ttsStrings.rhymes_with));
          await cancellablePromise(speakSyllables({
            phon: RHYME_PHON,
            onSpeak: (i) => setSyllable(rhymeSyllables[i]),
          }));
          await cancellablePromise(sleep());
          setSyllable(word);

          if(getUserData().bilingualMode) {
            await cancellablePromise(speak(ttsStrings.ask_word_2, true));
            await cancellablePromise(speak({textToSpeak: RHYME, skipTranslation: true}, true));
          }
          else await cancellablePromise(speak(`${ttsStrings.ask_word_2} "${word}".`));

          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      <div className="word-display-content">{renderContent()}</div>
      <div style={{ marginTop: 30 }}>
        <ListeningStatus />
      </div>
    </Container>
  );

  function renderContent() {
    if ([0, 2].includes(stage)) return <Word>{word}</Word>;
    if (stage === 1)
      return (
        <div className="rhyme-wrapper">
          <Word>{RHYME}</Word>
          <div className="rhymes-with">
            <div>rhymes</div>
            <div>with</div>
          </div>
          <Word>{word}</Word>
        </div>
      );
    if (stage === 3) return <Word>{syllable}</Word>;
  }
}
